import type {
  LedgerBalanceOutput,
  LedgerEntrySource,
  LedgerEntryType,
  PaginatedLedgerEntriesOutput,
  PaginatedPayoutsOutput,
  PayoutStatus,
} from '@sunrise-ui/api/events';
import { QueryKeys } from '../constants/QueryKeys';
import { assertParameterIsNotEmptyString } from '../utils';
import { ledgerApi, useAuthQuery } from '../utils/api';

const { ledgerKey, balanceKey, payoutsKey, entriesKey } = QueryKeys.ledger;

export const useGetLedgerBalance = (communityId?: string) =>
  useAuthQuery({
    queryKey: [ledgerKey, balanceKey, communityId],
    enabled: Boolean(communityId),
    queryFn: async (): Promise<LedgerBalanceOutput> => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await ledgerApi.getLedgerBalanceLedgerCommunitiesCommunityIdBalanceGet({
          communityId: communityId as string,
        });
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });

type GetCommunityPayoutsOptions = {
  limit?: number;
  offset?: number;
  status?: PayoutStatus;
};

export const useGetCommunityPayouts = (
  communityId?: string,
  options?: GetCommunityPayoutsOptions,
) => {
  const { limit = 10, offset = 0, status } = options ?? {};

  return useAuthQuery({
    queryKey: [ledgerKey, payoutsKey, communityId, { limit, offset, status }],
    enabled: Boolean(communityId),
    queryFn: async (): Promise<PaginatedPayoutsOutput> => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await ledgerApi.getCommunityPayoutsLedgerCommunitiesCommunityIdPayoutsGet(
          {
            communityId: communityId as string,
            limit,
            offset,
            status,
          },
        );
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });
};

type GetLedgerEntriesOptions = {
  limit?: number;
  offset?: number;
  entryType?: LedgerEntryType;
  source?: LedgerEntrySource;
  startDate?: string;
  endDate?: string;
};

export const useGetLedgerEntries = (
  communityId?: string,
  options?: GetLedgerEntriesOptions,
) => {
  const {
    limit = 10,
    offset = 0,
    entryType,
    source,
    startDate,
    endDate,
  } = options ?? {};

  return useAuthQuery({
    queryKey: [
      ledgerKey,
      entriesKey,
      communityId,
      { limit, offset, entryType, source, startDate, endDate },
    ],
    enabled: Boolean(communityId),
    queryFn: async (): Promise<PaginatedLedgerEntriesOutput> => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await ledgerApi.getLedgerEntriesLedgerCommunitiesCommunityIdEntriesGet({
          communityId: communityId as string,
          limit,
          offset,
          entryType,
          source,
          startDate,
          endDate,
        });
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });
};
